/*
 * Scrim Banner customizer.
 *
 * Pulls the pricing config from the DTF Town app proxy (single source of
 * truth in the Laravel `config/scrim_banner.php`), renders a guided step UI
 * (material -> size -> hanging -> details), shows a live price, and on
 * add-to-cart asks the server to mint a per-order priced variant which is
 * then added to the native cart. The server re-validates and re-prices every
 * request, so the client price is display-only and never trusted.
 */
(function () {
  'use strict';

  var root = document.getElementById('scrim-banner-app');
  if (!root) return;

  var PROXY = root.dataset.proxyBase || '/apps/coin-rewards';
  var CART_ADD_URL = root.dataset.cartAddUrl || '/cart/add';
  var COMING_SOON = root.dataset.comingSoonLabel || 'Coming soon';
  // When the product template locks the customizer to a single material, the
  // material step is hidden and that material is preselected.
  var LOCK_MATERIAL = (root.dataset.material || '').trim() || null;

  var cfg = null;
  var state = {
    material: null,
    width: null,
    height: null,
    hanging: 'none',
    placement: null,
    quantity: 1,
    instructions: '',
    edit: null // which completed step the user re-opened
  };

  // Required print-ready artwork. Kept outside `state` (which is JSON-encoded
  // to the pricing endpoint) because a File can't be serialised. Uploaded to
  // Shopify as a native line-item file property at add-to-cart.
  var artworkFile = null;
  var ARTWORK_EXTS = ['png', 'jpeg', 'jpg', 'bmp', 'gif', 'svg', 'psd', 'ai', 'pdf'];
  var ARTWORK_ACCEPT = '.' + ARTWORK_EXTS.join(',.');
  var ARTWORK_MAX_BYTES = 20 * 1024 * 1024; // Shopify line-item file upload cap

  var money = function (n) { return '$' + (Math.round(n * 100) / 100).toFixed(2); };
  var round2 = function (n) { return Math.round(n * 100) / 100; };

  function materialByKey(key) { return (cfg.materials || []).find(function (m) { return m.key === key; }); }
  function widthsFor(m) { return m ? Object.keys(m.prices).map(Number).sort(function (a, b) { return a - b; }) : []; }
  function heightsFor(m, w) { return (m && m.prices[w]) ? Object.keys(m.prices[w]).map(Number).sort(function (a, b) { return a - b; }) : []; }
  function widthActive(w) { return cfg.active_widths.indexOf(w) !== -1; }
  function hangingOption(key) { return (cfg.hanging_options || []).find(function (o) { return o.key === key; }); }

  function discountPercent(qty) {
    var pct = 0;
    (cfg.qty_tiers || []).forEach(function (t) { if (qty >= t.min) pct = t.percent; });
    return pct;
  }

  function activeTier(qty) {
    var active = null;
    (cfg.qty_tiers || []).forEach(function (t) { if (qty >= t.min) active = t; });
    return active;
  }

  function hangingApplies() {
    var m = materialByKey(state.material);
    return !!(m && m.hanging_enabled);
  }

  // Mirror of ScrimBannerService::quote, display only.
  function compute() {
    var m = materialByKey(state.material);
    if (!m || state.width == null || state.height == null) return null;
    if (!widthActive(state.width)) return null;
    var row = m.prices[state.width];
    if (!row || row[state.height] == null) return null;

    var base = row[state.height];
    var hang = hangingOption(state.hanging);
    var hangingApplied = false;
    if (hang && hang.grommets > 0 && m.hanging_enabled) {
      if (hang.needs_placement && !state.placement) return null;
      hangingApplied = true;
    }

    var unitPre = hangingApplied ? base * (1 + cfg.hanging_surcharge) : base;
    var pct = discountPercent(state.quantity);
    var unit = round2(unitPre * (1 - pct / 100));
    return { base: base, unit: unit, total: round2(unit * state.quantity), percent: pct, hangingApplied: hangingApplied };
  }

  /* -------------------------------------------------------------- step model */

  function steps() {
    var list = [];
    // Material is always shown as a product switcher: the current product's
    // material is preselected and clicking another navigates to that product.
    list.push({ id: 'material', title: 'Material' });
    list.push({ id: 'size', title: 'Size (W x H, feet)' });
    if (hangingApplies()) list.push({ id: 'hanging', title: 'Hanging' });
    list.push({ id: 'artwork', title: 'Upload Artwork' });
    list.push({ id: 'details', title: 'Quantity & Notes' });
    return list;
  }

  function isDone(id) {
    var m = materialByKey(state.material);
    if (id === 'material') return !!state.material;
    if (id === 'size') return !!(m && state.width != null && widthActive(state.width) && m.prices[state.width] && m.prices[state.width][state.height] != null);
    if (id === 'hanging') {
      if (!isDone('size')) return false;
      var o = hangingOption(state.hanging);
      if (!o) return false;
      return !(o.needs_placement && o.grommets > 0) || !!state.placement;
    }
    if (id === 'artwork') return isUnlocked('artwork') && !!artworkFile;
    if (id === 'details') return isDone('artwork');
    return false;
  }

  function isUnlocked(id) {
    if (id === 'material') return true;
    if (id === 'size') return isDone('material');
    if (id === 'hanging') return isDone('size');
    if (id === 'artwork') return isDone('size') && (!hangingApplies() || isDone('hanging'));
    if (id === 'details') return isDone('artwork');
    return false;
  }

  function currentStepId() {
    var s = steps();
    for (var i = 0; i < s.length; i++) {
      if (isUnlocked(s[i].id) && !isDone(s[i].id)) return s[i].id;
    }
    return null;
  }

  function summaryFor(id) {
    var m = materialByKey(state.material);
    if (id === 'material') return m ? m.label : '';
    if (id === 'size') return state.width + ' ft × ' + state.height + ' ft';
    if (id === 'hanging') {
      var o = hangingOption(state.hanging);
      if (!o || o.grommets === 0) return 'None';
      return o.label + (state.placement ? ' · ' + (hangingOption(state.hanging).needs_placement ? placementLabel(state.placement) : '') : '');
    }
    if (id === 'artwork') return artworkFile ? artworkFile.name : '';
    if (id === 'details') {
      var t = 'Qty ' + state.quantity;
      if (state.instructions.trim()) t += ' · notes added';
      return t;
    }
    return '';
  }

  function placementLabel(key) {
    var p = (cfg.hanging_placements || []).find(function (x) { return x.key === key; });
    return p ? p.label : '';
  }

  /* ---------------------------------------------------------------- render */

  function el(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }

  function optionButton(label, active, onClick, small) {
    var b = el('button', 'scrim-option' + (active ? ' is-active' : '') + (small ? ' scrim-option--sm' : ''));
    b.type = 'button';
    b.textContent = label;
    b.addEventListener('click', onClick);
    return b;
  }

  function render() {
    if (!cfg) return;
    root.innerHTML = '';

    var list = steps();
    var current = currentStepId();
    var stepNo = 0;

    list.forEach(function (step) {
      stepNo++;
      var unlocked = isUnlocked(step.id);
      var done = isDone(step.id);
      // Material is a product switcher, so keep it open (never collapse it to a
      // one-line summary) — the customer should always see the other products.
      var expanded = unlocked && (!done || step.id === current || step.id === state.edit || step.id === 'material');

      var wrap = el('div', 'scrim-step' + (unlocked ? '' : ' is-locked') + (done && !expanded ? ' is-done' : '') + (expanded ? ' is-open' : ''));

      var head = el('div', 'scrim-step-head');
      var badge = el('span', 'scrim-step-num', done && !expanded ? '✓' : String(stepNo));
      var title = el('span', 'scrim-step-title', step.title);
      head.appendChild(badge);
      head.appendChild(title);

      if (done && !expanded) {
        head.appendChild(el('span', 'scrim-step-summary', summaryFor(step.id)));
        head.appendChild(el('span', 'scrim-step-edit', 'Edit'));
        head.classList.add('is-clickable');
        head.addEventListener('click', function () { state.edit = step.id; render(); });
      } else if (!unlocked) {
        head.appendChild(el('span', 'scrim-step-hint', step.id === 'size' ? 'Select a material first' : 'Complete the step above'));
      }
      wrap.appendChild(head);

      if (expanded) {
        wrap.appendChild(buildBody(step.id));
      }
      root.appendChild(wrap);
    });

    root.appendChild(buildFooter());
    update();
  }

  function buildBody(id) {
    var body = el('div', 'scrim-step-body');
    var m = materialByKey(state.material);

    if (id === 'material') {
      var grid = el('div', 'scrim-options');
      cfg.materials.forEach(function (mt) {
        var isCurrent = state.material === mt.key;
        var b = el('button', 'scrim-option scrim-material' + (isCurrent ? ' is-current' : ''));
        b.type = 'button';
        b.appendChild(el('span', 'scrim-material-label', mt.label));
        if (isCurrent) {
          b.appendChild(el('span', 'scrim-material-badge', '✓ You\u2019re here'));
          b.setAttribute('aria-current', 'true');
        } else {
          b.appendChild(el('span', 'scrim-material-go', 'View product \u2192'));
        }
        b.addEventListener('click', function () {
          if (isCurrent) return; // already on this product
          // Each material is its own product — go to that product's page.
          if (mt.url) { window.location.href = mt.url; return; }
          // Fallback (no dedicated product configured): select in place.
          state.material = mt.key;
          state.width = null; state.height = null; state.hanging = 'none'; state.placement = null;
          state.edit = null;
          render();
        });
        grid.appendChild(b);
      });
      body.appendChild(grid);

      // Reassure the customer which product they're on and that they can move on.
      var cur = materialByKey(state.material);
      if (cur) {
        body.appendChild(el('p', 'scrim-material-note scrim-material-note--ok',
          '✓ You\u2019re customizing <strong>' + cur.label + '</strong> — you can continue to the next step below.'));
      }
      // Strong warning: materials are different products, not variants.
      body.appendChild(el('p', 'scrim-material-note scrim-material-note--warn',
        '⚠️ Each material is a <strong>separate product</strong>. Choosing a different one above will take you to that product\u2019s page — please make sure you\u2019re ordering the right material.'));
    }

    if (id === 'size') {
      var row = el('div', 'scrim-size-row');
      var wSel = el('select', 'scrim-select');
      wSel.appendChild(new Option('Width', ''));
      widthsFor(m).forEach(function (w) {
        var active = widthActive(w);
        var o = new Option(w + ' ft' + (active ? '' : ' (' + COMING_SOON + ')'), w);
        if (!active) o.disabled = true;
        if (state.width === w) o.selected = true;
        wSel.appendChild(o);
      });
      wSel.addEventListener('change', function () {
        state.width = this.value ? Number(this.value) : null;
        state.height = null; state.edit = 'size';
        render();
      });

      var hSel = el('select', 'scrim-select');
      hSel.appendChild(new Option('Height', ''));
      if (state.width != null) {
        heightsFor(m, state.width).forEach(function (h) {
          var o = new Option(h + ' ft', h);
          if (state.height === h) o.selected = true;
          hSel.appendChild(o);
        });
      } else {
        hSel.disabled = true;
      }
      hSel.addEventListener('change', function () {
        state.height = this.value ? Number(this.value) : null;
        state.edit = null;
        render();
      });

      row.appendChild(wSel);
      row.appendChild(el('span', 'scrim-times', '&times;'));
      row.appendChild(hSel);
      body.appendChild(row);
      body.appendChild(el('p', 'scrim-note', 'Pick a width, then a height to see your price.'));
    }

    if (id === 'hanging') {
      var hGrid = el('div', 'scrim-options scrim-options--stack');
      cfg.hanging_options.forEach(function (o) {
        hGrid.appendChild(optionButton(o.label, state.hanging === o.key, function () {
          state.hanging = o.key;
          if (!o.needs_placement || o.grommets === 0) { state.placement = null; state.edit = null; }
          render();
        }));
      });
      body.appendChild(hGrid);

      var ho = hangingOption(state.hanging);
      if (ho && ho.needs_placement && ho.grommets > 0) {
        var sub = el('div', 'scrim-subfield');
        sub.appendChild(el('div', 'scrim-sublabel', 'Where should the grommets go?'));
        var pGrid = el('div', 'scrim-options');
        cfg.hanging_placements.forEach(function (p) {
          pGrid.appendChild(optionButton(p.label, state.placement === p.key, function () {
            state.placement = p.key; state.edit = null; render();
          }, true));
        });
        sub.appendChild(pGrid);
        body.appendChild(sub);
      }
      body.appendChild(el('p', 'scrim-note', 'Hanging adds grommets (+10%). Choose "I don\u2019t want hanging options" to skip.'));
    }

    if (id === 'artwork') {
      var up = el('div', 'scrim-upload');
      var label = el('label', 'scrim-upload-btn');
      label.appendChild(el('span', null, artworkFile ? 'Choose a different file' : 'Choose file'));
      var input = el('input', 'scrim-file');
      input.type = 'file';
      input.accept = ARTWORK_ACCEPT;
      input.addEventListener('change', function () {
        var f = this.files && this.files[0];
        if (!f) { return; }
        var ext = (f.name.split('.').pop() || '').toLowerCase();
        if (ARTWORK_EXTS.indexOf(ext) === -1) {
          artworkFile = null; this.value = '';
          showError('Unsupported file type. Allowed: ' + ARTWORK_EXTS.join(', ').toUpperCase() + '.');
          update(); return;
        }
        if (f.size > ARTWORK_MAX_BYTES) {
          artworkFile = null; this.value = '';
          showError('That file is too large (max 20 MB).');
          update(); return;
        }
        showError('');
        artworkFile = f;
        state.edit = null;
        render();
      });
      label.appendChild(input);
      up.appendChild(label);
      if (artworkFile) {
        up.appendChild(el('p', 'scrim-upload-file', '✓ ' + artworkFile.name));
      }
      body.appendChild(up);
      body.appendChild(el('p', 'scrim-note', 'Upload your print-ready artwork (required). Accepted: PNG, JPG, JPEG, BMP, GIF, SVG, PSD, AI, PDF. Max 20 MB.'));
      return body;
    }

    if (id === 'details') {
      var qtyWrap = el('div', 'scrim-inline');
      qtyWrap.appendChild(el('label', 'scrim-inline-label', 'Quantity'));
      var qty = el('input', 'scrim-qty');
      qty.type = 'number'; qty.min = '1'; qty.value = state.quantity;
      qty.addEventListener('input', function () {
        var v = parseInt(this.value, 10);
        state.quantity = isNaN(v) || v < 1 ? 1 : v;
        update();
      });
      qtyWrap.appendChild(qty);
      body.appendChild(qtyWrap);

      var tiers = cfg.qty_tiers.filter(function (t) { return t.percent > 0; });
      if (tiers.length) {
        var dl = el('div', 'scrim-disc-grid');
        tiers.forEach(function (t) {
          var c = el('div', 'scrim-disc-card');
          c.dataset.min = t.min;
          c.appendChild(el('div', 'scrim-disc-qty', t.min + '+'));
          c.appendChild(el('div', 'scrim-disc-off', '-' + t.percent + '%'));
          dl.appendChild(c);
        });
        body.appendChild(el('div', 'scrim-sublabel', 'Buy more, save more (same design & size)'));
        body.appendChild(dl);
      }

      body.appendChild(el('label', 'scrim-sublabel', 'Specific instructions (optional)'));
      var ta = el('textarea', 'scrim-textarea');
      ta.rows = 2; ta.maxLength = 1000;
      ta.placeholder = 'Anything we should know about your banner?';
      ta.value = state.instructions;
      ta.addEventListener('input', function () { state.instructions = this.value; });
      body.appendChild(ta);
    }

    return body;
  }

  function buildFooter() {
    var footer = el('div', 'scrim-footer');
    var pw = el('div', 'scrim-price-wrap');
    var price = el('div', 'scrim-price', '—'); price.id = 'scrimPrice';
    var sub = el('div', 'scrim-price-sub', ''); sub.id = 'scrimPriceSub';
    pw.appendChild(price); pw.appendChild(sub);

    var btn = el('button', 'scrim-add-btn'); btn.type = 'button'; btn.id = 'scrimAddBtn';
    btn.textContent = 'Add to Cart';
    btn.addEventListener('click', addToCart);

    footer.appendChild(pw);
    footer.appendChild(btn);

    var err = el('div', 'scrim-error'); err.id = 'scrimError';
    var box = el('div', 'scrim-footer-box');
    box.appendChild(footer);
    box.appendChild(err);
    return box;
  }

  function nextHint() {
    if (!state.material) return 'Choose a material to start';
    if (!isDone('size')) return state.width == null ? 'Choose a width' : 'Choose a height';
    if (hangingApplies() && !isDone('hanging')) return 'Choose where the grommets go';
    return '';
  }

  function updateTierHighlight() {
    var active = activeTier(state.quantity);
    document.querySelectorAll('.scrim-disc-card').forEach(function (c) {
      var on = !!active && active.percent > 0 && Number(c.dataset.min) === active.min;
      c.classList.toggle('is-active', on);
    });
  }

  function update() {
    var price = document.getElementById('scrimPrice');
    var sub = document.getElementById('scrimPriceSub');
    var btn = document.getElementById('scrimAddBtn');
    updateTierHighlight();
    if (!price) return;

    var q = compute();
    if (!q) {
      price.textContent = '—';
      sub.textContent = nextHint();
      sub.classList.add('is-hint');
      if (btn) btn.disabled = true;
      return;
    }
    price.textContent = money(q.total);
    var bits = [];
    if (state.quantity > 1) bits.push(money(q.unit) + ' / unit × ' + state.quantity);
    if (q.percent > 0) bits.push(q.percent + '% off');
    if (q.hangingApplied) bits.push('incl. hanging');

    // Artwork is required before checkout (doc requirement).
    if (!artworkFile) {
      sub.textContent = 'Upload your artwork to continue';
      sub.classList.add('is-hint');
      if (btn) btn.disabled = true;
      return;
    }
    sub.classList.remove('is-hint');
    sub.textContent = bits.join(' · ');
    if (btn) btn.disabled = false;
  }

  function showError(msg) {
    var err = document.getElementById('scrimError');
    if (err) err.textContent = msg || '';
  }

  /* -------------------------------------------------------------- add cart */

  function addToCart() {
    var btn = document.getElementById('scrimAddBtn');
    var q = compute();
    if (!q || !btn) return;
    if (!artworkFile) { showError('Please upload your artwork before adding to cart.'); return; }
    showError('');
    btn.disabled = true;
    btn.classList.add('is-loading');

    fetch(PROXY + '/scrim/variant', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(state)
    })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error(res.data && res.data.error ? res.data.error : 'Could not build your banner.');
        return addVariantToCart(res.data);
      })
      .catch(function (e) { showError(e.message || 'Something went wrong. Please try again.'); })
      .finally(function () { btn.disabled = false; btn.classList.remove('is-loading'); });
  }

  function getSectionsToRender() {
    return [
      { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
      { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
      { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
    ];
  }

  function addVariantToCart(data) {
    var form = new FormData();
    form.append('id', data.variant_id);
    form.append('quantity', data.quantity || state.quantity);
    if (data.properties) {
      Object.keys(data.properties).forEach(function (k) { form.append('properties[' + k + ']', data.properties[k]); });
    }
    // Required artwork as a native Shopify line-item file property. Shopify
    // stores the file and the property value becomes a CDN URL the printer can
    // open from the order. Visible to the customer in the cart (no underscore).
    if (artworkFile) {
      form.append('properties[Artwork]', artworkFile, artworkFile.name);
    }
    // Hidden flag (underscore prefix => not shown to the customer) so the cart
    // and drawer can lock the quantity input for these lines.
    form.append('properties[_scrim_banner]', '1');
    form.append('sections', getSectionsToRender().map(function (s) { return s.section; }).join(','));
    form.append('sections_url', window.location.pathname);

    return fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
      body: form
    })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        if (response.status) throw new Error(response.description || response.message || 'Could not add to cart.');
        renderCart(response);
      });
  }

  function renderCart(parsedState) {
    var rendered = false;
    if (parsedState.sections) {
      getSectionsToRender().forEach(function (section) {
        var container = document.getElementById(section.id);
        if (!container || !parsedState.sections[section.section]) return;
        var target = container.querySelector(section.selector) || container;
        var html = new DOMParser().parseFromString(parsedState.sections[section.section], 'text/html');
        var source = html.querySelector(section.selector) || html.querySelector('#' + section.id);
        if (source) { target.innerHTML = source.innerHTML; rendered = true; }
      });
    }
    if (typeof CartDrawer !== 'undefined') { try { new CartDrawer(); } catch (e) {} }
    if (typeof Cart !== 'undefined') { try { new Cart().renderContents(parsedState); } catch (e) {} }

    var drawer = document.getElementById('Cart-Drawer');
    if (rendered && drawer && typeof drawer.open === 'function') {
      drawer.open();
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } else {
      window.location.href = (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart';
    }
  }

  /* ------------------------------------------------------------------ boot */

  document.addEventListener('click', function (e) {
    var thumb = e.target.closest('.scrim-banner-thumb');
    if (!thumb) return;
    var main = document.getElementById('scrimMainImage');
    if (main && thumb.dataset.full) main.src = thumb.dataset.full;
    document.querySelectorAll('.scrim-banner-thumb').forEach(function (t) { t.classList.remove('is-active'); });
    thumb.classList.add('is-active');
  });

  // Pricing config changes rarely, so cache it in localStorage and render from
  // the cache instantly on load while we revalidate in the background. This
  // makes the customizer open immediately on repeat visits instead of waiting
  // for the app-proxy round-trip. The server re-prices every add-to-cart, so a
  // briefly stale cached price is display-only and never trusted.
  var CACHE_KEY = 'scrimPricingV1';
  var lastCfgStr = null;

  // Which material this product is. Prefer the template's locked material, but
  // fall back to matching the current product handle against each material's
  // URL — so the right material stays preselected even if the template setting
  // was never pushed live.
  function detectMaterial() {
    if (LOCK_MATERIAL && materialByKey(LOCK_MATERIAL)) return LOCK_MATERIAL;
    var handle = (window.location.pathname || '').split('/').filter(Boolean).pop() || '';
    var found = (cfg.materials || []).find(function (m) {
      return m.url && m.url.split('/').pop() === handle;
    });
    return found ? found.key : null;
  }

  function applyConfig(data) {
    cfg = data;
    var current = detectMaterial();
    if (current) {
      state.material = current;
      LOCK_MATERIAL = current;
    } else {
      LOCK_MATERIAL = null;
    }
    render();
  }

  try {
    var cachedStr = localStorage.getItem(CACHE_KEY);
    if (cachedStr) { lastCfgStr = cachedStr; applyConfig(JSON.parse(cachedStr)); }
  } catch (e) {}

  fetch(PROXY + '/scrim/pricing', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
    .then(function (r) { if (!r.ok) throw new Error('pricing fetch failed'); return r.json(); })
    .then(function (data) {
      var str = JSON.stringify(data);
      try { localStorage.setItem(CACHE_KEY, str); } catch (e) {}
      // Only re-render when the config actually changed, so a background refresh
      // never disrupts a customer who is mid-interaction.
      if (str !== lastCfgStr) { lastCfgStr = str; applyConfig(data); }
    })
    .catch(function () {
      if (!cfg) root.innerHTML = '<div class="scrim-banner-loading">Customizer is unavailable right now. Please refresh.</div>';
    });
})();
